/* eslint-disable no-unexpected-multiline */
import { Inject, Injectable } from '@angular/core';
import { AuthService, UserRole } from './auth.service';
import {
  AdminTenantSettingApiModel,
  AdminTenantSettingKey,
  BASE_PATH,
  SettingService,
  TenantSettingApiModel,
  TenantSettingKey,
  UserSettingApiModel,
  UserSettingKey
} from '../../api-new';
import {Observable, Subject} from 'rxjs';
import {map, tap} from 'rxjs/operators';

type AdminTenantSettingKeyType = (typeof AdminTenantSettingKey)[keyof typeof AdminTenantSettingKey];
type UserSettingKeyType   = (typeof UserSettingKey)[keyof typeof UserSettingKey];
type TenantSettingKeyType = (typeof TenantSettingKey)[keyof typeof TenantSettingKey];

@Injectable({
  providedIn: 'root'
})
export class UserDetailService {

  userName?: string;
  tenantName?: string;
  profilePicture?: string;
  role?: UserRole;
  isAccountAdmin?: boolean;
  tenantId?: string;
  userID?: string;
  readonly settingsUpdated$ = new Subject<void>();
  userSettings: UserSettingApiModel[] = [];
  tenantSettings: TenantSettingApiModel[] = [];
  adminTenantSettings: AdminTenantSettingApiModel[] = [];
  private userSettingsMap = new Map<string, string>();
  private tenantSettingsMap = new Map<string, string>();
  private adminTenantSettingsMap = new Map<string, string>();

  constructor(
    private authService: AuthService,
    @Inject(BASE_PATH) private basePath: string,
    private settingService: SettingService,
  ) {
    authService.loggedIn.subscribe(
      (status) => {
        if (status) {
          const payload = authService.jwt ? JSON.parse(decodeURIComponent(escape(atob(authService.jwt?.split('.')
          [1].replace(/-/g, '+').replace(/_/g, '/'))))) : undefined;

          if (payload && payload.isAccountAdmin === undefined) {
            this.authService.logOut();
            return;
          }
          if (payload) {
            this.userName = payload.unique_name;
            this.tenantName = payload.tenantName;
            this.role = payload.role;
            this.tenantId = payload.tenantId;
            this.isAccountAdmin = payload.isAccountAdmin == 'True';
            this.userID = payload.userID;
          }
          this.profilePicture = 'https://ui-avatars.com/api/?background=cccccc&rounded=true&name=' + this.userName?.replaceAll(' ', '+');
          this.loadAndNotifySettings();
        } else {
          this.clearIdentity();
        }
      }
    );
  }

  getUserSetting(key: UserSettingKeyType): string | undefined {
    return this.userSettingsMap.get(String(key));
  }

  getTenantSetting(key: TenantSettingKeyType): string | undefined {
    return this.tenantSettingsMap.get(String(key));
  }

  getAdminTenantSetting(key: AdminTenantSettingKeyType): string | undefined {
    return this.adminTenantSettingsMap.get(String(key));
  }

  areCoursesEnabled(): boolean {
    return this.getAdminTenantSetting(AdminTenantSettingKey.COURSES_ENABLED)?.toLowerCase() === 'true';
  }

  areSubOrganisationsEnabled(): boolean {
    return this.getAdminTenantSetting(AdminTenantSettingKey.SUBORGANISATION_ENABLED)?.toLowerCase() === 'true';
  }



  refreshSettings(): Observable<void> {
    return this.settingService.settingGetUserSettings(+this.userID!).pipe(
      tap(setting => this.applySettingPayload(
        setting.userSettings ?? [],
        setting.tenantSettings ?? [],
        setting.adminTenantSettings ?? []
      )),
      map(() => void 0)
    );
  }

  private loadAndNotifySettings(): void {
    if (!this.userID) return;
    this.settingService.settingGetUserSettings(+this.userID).subscribe(setting => {
      this.applySettingPayload(
        setting.userSettings ?? [],
        setting.tenantSettings ?? [],
        setting.adminTenantSettings ?? []
      );
    });
  }

  private applySettingPayload(
    user: UserSettingApiModel[],
    tenant: TenantSettingApiModel[],
    adminTenant: AdminTenantSettingApiModel[]
  ): void {
    this.userSettings = user;
    this.tenantSettings = tenant;
    this.adminTenantSettings = adminTenant;
    this.rebuildMaps();
    this.settingsUpdated$.next();
  }

  private rebuildMaps(): void {
    this.userSettingsMap.clear();
    this.tenantSettingsMap.clear();
    this.adminTenantSettingsMap.clear();
    for (const s of this.userSettings) this.userSettingsMap.set(String(s.key), s.value ?? '');
    for (const s of this.tenantSettings) this.tenantSettingsMap.set(String(s.key), s.value ?? '');
    for (const s of this.adminTenantSettings) this.adminTenantSettingsMap.set(String(s.key), s.value ?? '');
  }

  private clearIdentity(): void {
    this.userName = undefined;
    this.profilePicture = undefined;
    this.role = undefined;
    this.isAccountAdmin = undefined;
    this.tenantId = undefined;
    this.userID = undefined;
    this.userSettings = [];
    this.tenantSettings = [];
    this.adminTenantSettings = [];
    this.userSettingsMap.clear();
    this.tenantSettingsMap.clear();
    this.adminTenantSettingsMap.clear();
    this.settingsUpdated$.next();
  }

}
